<template>
  <div class="pa-2">
    <system-health-banner
      title="Scheduled Jobs"
      :subtitle="bannerSubtitle"
      :status="summary.status || 'unknown'"
    />

    <div v-if="nonHealthyItems.length" class="mb-2">
      <v-list dense>
        <v-list-tile
          v-for="item in nonHealthyItems"
          :key="item.id"
          class="diagnostics-job-item"
        >
          <v-list-tile-action>
            <v-icon :color="item.hasLastError ? 'error' : 'warning'">
              {{ item.hasLastError ? 'fal fa-circle-exclamation' : 'fal fa-triangle-exclamation' }}
            </v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.jobCode }}</v-list-tile-title>
            <v-list-tile-sub-title>
              {{ nonHealthyReason(item) }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action>
            <v-btn
              small
              color="warning"
              outline
              :loading="isRunning(item.id)"
              :disabled="isRunning(item.id)"
              @click="openRunNowConfirmation(item)"
            >
              Run now
            </v-btn>
          </v-list-tile-action>
        </v-list-tile>
      </v-list>
    </div>

    <v-alert
      v-else
      :value="true"
      type="success"
      outline
      class="mb-2"
    >
      All scheduled jobs are running on schedule.
    </v-alert>

    <v-btn
      outline
      color="primary"
      :loading="apiInProgress"
      :disabled="apiInProgress"
      @click="detailDialog = true"
    >
      Show all scheduled jobs
    </v-btn>

    <detail-dialog
      v-model="detailDialog"
      title="Scheduled Jobs"
      :max-width="1200"
    >
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="apiInProgress"
        :rows-per-page-items="[10, 25, 50, 100]"
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>{{ props.item.jobCode }}</td>
            <td>{{ props.item.jobStatus }}</td>
            <td>{{ props.item.jobCron }}</td>
            <td>{{ formatDateTime(props.item.dateLastRun) }}</td>
            <td>{{ formatDateTime(props.item.dateNextRun) }}</td>
            <td class="truncate-cell">{{ extractLastError(props.item) || '-' }}</td>
            <td class="text-xs-right">
              <v-btn
                small
                color="warning"
                outline
                :loading="isRunning(props.item.id)"
                :disabled="isRunning(props.item.id)"
                @click="openRunNowConfirmation(props.item)"
              >
                Run now
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </detail-dialog>

    <v-dialog v-model="runNowConfirmDialog" max-width="520">
      <v-card>
        <v-card-title>
          <span class="headline">Run Scheduled Job Now?</span>
        </v-card-title>
        <v-card-text>
          <p>
            This will queue <strong>{{ selectedRunNowJobLabel }}</strong> to run on the next scheduler worker tick.
          </p>
          <p class="mb-0">
            Continue only if you expect this job to run immediately.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="runningJobId !== null" @click="closeRunNowConfirmation">Cancel</v-btn>
          <v-btn
            color="warning"
            :loading="selectedRunNowJob && isRunning(selectedRunNowJob.id)"
            :disabled="!selectedRunNowJob || runningJobId !== null"
            @click="confirmRunNow"
          >
            Run now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatDate, formatTime } from '@smarttransit/common'
import {
  getApiScheduledJobs,
  runApiScheduledJob
} from '@/services/diagnostics-service'
import { addAlert } from '@/utilities/helpers'
import SystemHealthBanner from '../components/SystemHealthBanner.vue'
import DetailDialog from '../components/DetailDialog.vue'

export default {
  name: 'realtime-diagnostics-scheduled-jobs-tab',
  components: {
    SystemHealthBanner,
    DetailDialog
  },
  props: {
    refreshToken: {
      type: Number,
      default: 0
    },
    silentRefresh: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      apiInProgress: false,
      runningJobId: null,
      detailDialog: false,
      runNowConfirmDialog: false,
      selectedRunNowJob: null,
      summary: {
        status: 'unknown',
        failingCount: 0,
        overdueCount: 0
      },
      items: [],
      headers: [
        { text: 'Job Code', value: 'jobCode' },
        { text: 'Status', value: 'jobStatus' },
        { text: 'Cron', value: 'jobCron' },
        { text: 'Last Run', value: 'dateLastRun' },
        { text: 'Next Run', value: 'dateNextRun' },
        { text: 'Last Error', value: 'lastError', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    bannerSubtitle () {
      const failing = this.summary.failingCount || 0
      const overdue = this.summary.overdueCount || 0
      if (failing > 0 || overdue > 0) {
        return failing + ' failing, ' + overdue + ' overdue'
      }
      return 'All healthy'
    },
    nonHealthyItems () {
      return this.items.filter(function (item) {
        return item.hasLastError || item.isOverdue
      })
    },
    selectedRunNowJobLabel () {
      return this.selectedRunNowJob && this.selectedRunNowJob.jobCode
        ? this.selectedRunNowJob.jobCode
        : 'this scheduled job'
    }
  },
  watch: {
    refreshToken: {
      immediate: true,
      handler () {
        this.loadData(this.silentRefresh)
      }
    }
  },
  methods: {
    async loadData (silent) {
      if (!silent) {
        this.apiInProgress = true
      }

      try {
        const response = await getApiScheduledJobs({ limit: 500, offset: 0, includeAll: false })
        this.summary = response && response.summary ? response.summary : this.summary
        this.items = response && response.items ? response.items : []
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to load scheduled jobs diagnostics'
        })
      } finally {
        if (!silent) {
          this.apiInProgress = false
        }
      }
    },
    formatDateTime (value) {
      if (!value) {
        return '-'
      }
      return formatDate(value) + ', ' + formatTime(value)
    },
    extractLastError (item) {
      if (!item || !item.metadata) {
        return ''
      }
      if (item.metadata.lastError) {
        return item.metadata.lastError
      }
      if (item.metadata.lastResult && item.metadata.lastResult.error) {
        return item.metadata.lastResult.error
      }
      return ''
    },
    nonHealthyReason (item) {
      const reasons = []
      if (item.hasLastError) {
        reasons.push('Last error: ' + this.extractLastError(item))
      }
      if (item.isOverdue) {
        reasons.push('Overdue by ' + Number(item.overdueBySeconds || 0) + 's')
      }
      return reasons.join(' • ')
    },
    isRunning (jobId) {
      return this.runningJobId && String(this.runningJobId) === String(jobId)
    },
    openRunNowConfirmation (item) {
      if (!item || !item.id) {
        return
      }
      this.selectedRunNowJob = item
      this.runNowConfirmDialog = true
    },
    closeRunNowConfirmation () {
      if (this.runningJobId !== null) {
        return
      }
      this.runNowConfirmDialog = false
      this.selectedRunNowJob = null
    },
    confirmRunNow () {
      if (!this.selectedRunNowJob) {
        return
      }
      this.runNow(this.selectedRunNowJob)
    },
    async runNow (item) {
      if (!item || !item.id) {
        return
      }
      this.runningJobId = item.id
      try {
        await runApiScheduledJob(item.id)
        addAlert({ type: 'success', message: 'Scheduled job queued to run now' })
        this.runNowConfirmDialog = false
        this.selectedRunNowJob = null
        await this.loadData(false)
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to run scheduled job'
        })
      } finally {
        this.runningJobId = null
      }
    }
  }
}
</script>

<style scoped>
.truncate-cell {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
